import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import className from 'classnames';
import React, {ReactNode} from 'react';

import {noSpacingBottom, spacingBottom} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Label from 'ui-library/bs/Label';
import {GRAY_DARK} from 'ui-library/Colors';

export type RadioButtonOptionType = string | boolean | number | symbol;

export interface RadioButtonSelectorProps<TValue extends RadioButtonOptionType> {
  disabled?: boolean;
  hasBottomSpacing?: boolean;
  // May be null/undefined, in which case none of the radio buttons is selected.
  selectedValue?: TValue | null;
  options: Array<{
    /** A label to be rendered directly next to the radio button. */
    label: string | JSX.Element;
    /** A secondary, faded label with supporting details for the option. */
    secondaryLabel?: ReactNode;
    value: TValue;
    dataTestAttrs?: DataTestAttrs;
  }>;
  onChange: (value: TValue) => void;
  // Optional: useful for implementing toggle-off behavior (unselecting a radio button)
  onClick?: (value: TValue) => void;
  isVertical?: boolean | null;
}

export interface RadioButtonGroupProps<T extends RadioButtonOptionType> extends RadioButtonSelectorProps<T> {
  title: string;
  hasBottomSpacing?: boolean;
}

export function RadioButtonSelector<TValue extends RadioButtonOptionType>({
  disabled = false,
  ...otherProps
}: RadioButtonSelectorProps<TValue>): JSX.Element {
  return (
    <div>
      {otherProps.options.map((option, i) => (
        <div
          key={String(option.value)}
          className={className(otherProps.isVertical ? 'u-display-block' : 'u-display-inline-block', {
            // Put spacing to the right of all options except the last so that the buttons will look
            // ok regardless of whether or not they are wrapped.
            'u-spacing-right': !otherProps.isVertical && i < otherProps.options.length - 1,
          })}
          {...getTestProps(option.dataTestAttrs)}
        >
          <label className="u-normal-font-weight u-no-wrap">
            <input
              checked={otherProps.selectedValue === option.value}
              className={css(styles.input)}
              onChange={() => {
                otherProps.onChange(option.value);
              }}
              onClick={
                otherProps.onClick != null
                  ? () => {
                      otherProps.onClick!(option.value);
                    }
                  : noop
              }
              disabled={disabled}
              type="radio"
              value={String(option.value)}
            />
            <span className="u-tiny-spacing-left">{option.label}</span>
            {option.secondaryLabel != null && (
              <div className={css(styles.secondaryLabel)}>{option.secondaryLabel}</div>
            )}
          </label>
        </div>
      ))}
    </div>
  );
}

export default function RadioButtonGroup<TValue extends RadioButtonOptionType>({
  title,
  hasBottomSpacing = true,
  ...remainingProps
}: RadioButtonGroupProps<TValue>): JSX.Element {
  return (
    <div
      className={css(hasBottomSpacing ? styles.buttonGroupBottomSpacing : styles.buttonGroupNoBottomSpacing)}
    >
      <Label className="u-display-block">{title}</Label>
      <RadioButtonSelector {...remainingProps} />
    </div>
  );
}

const styles = StyleSheet.create({
  buttonGroupBottomSpacing: spacingBottom,
  buttonGroupNoBottomSpacing: noSpacingBottom,
  input: {
    marginTop: '2px',
    verticalAlign: 'top',
  },
  secondaryLabel: {
    marginLeft: '18px',
    color: GRAY_DARK,
    whiteSpace: 'normal',
  },
});
